import type { CSSProperties } from 'react';
import { chatThemeCssVars, getChatThemeTokens, type IdigestChatTheme } from '../theme';

/**
 * Shared Fluent modal shell for list/feature dialogs.
 * Use fixed px width (not vw %) so vine-web narrow columns don't crush
 * header icon buttons / labels.
 */
export const COMMON_MODAL_STYLES = {
  borderRadius: '20px',
  minWidth: '320px',
  width: '480px',
  maxWidth: 'calc(100vw - 32px)',
  // Grow with content; cap height so short lists aren't full-viewport blanks.
  height: 'auto',
  maxHeight: 'min(720px, calc(100vh - 80px))',
  marginTop: '40px',
  marginBottom: '40px',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  selectors: {
    '@media (max-width: 520px)': {
      width: '100vw',
      minWidth: 0,
      maxWidth: 'none',
      borderRadius: '16px',
      marginTop: '0',
      marginBottom: '0',
      maxHeight: '100vh'
    },
    '@media (max-height: 500px)': {
      maxHeight: '100vh',
      borderRadius: 0,
      marginTop: 0,
      marginBottom: 0
    }
  }
};

/** Scrollable list region — matches fellowship list (no bordered box).
 * Horizontal inset comes from `group-list-pane-pad` on the wrapper. */
export const COMMON_MODAL_LIST_STYLE: CSSProperties = {
  flex: '1 1 auto',
  minHeight: 120,
  maxHeight: 'min(360px, calc(100vh - 240px))',
  overflowY: 'auto',
  overflowX: 'hidden',
  border: 'none',
  borderRadius: 0,
  padding: '0 0 8px',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0
};

/**
 * Fluent `modalProps.styles.main` with theme CSS variables so portal dialogs
 * (outside `.idigest-chat`) still inherit dialog tokens.
 */
export function getThemedModalMainStyles(theme: IdigestChatTheme = 'light', overrides: Record<string, unknown> = {}) {
  const tokens = getChatThemeTokens(theme);
  return {
    ...COMMON_MODAL_STYLES,
    backgroundColor: tokens.dialogBg,
    color: tokens.dialogFg,
    ...chatThemeCssVars(tokens),
    ...overrides
  };
}

export function getDialogThemeClassName(theme: IdigestChatTheme = 'light') {
  return [
    'PickMeetingDialogue',
    'idigest-chat-dialog',
    // Reuse shell list/search token rules (group-list-pane-pad, orgGroupButton, …)
    'idigest-chat',
    theme === 'dark' ? 'idigest-chat--dark' : 'idigest-chat--light'
  ].join(' ');
}
